/**
 * Drawer component - migrated from @praetorian-chariot/ui
 *
 * Migration reason: Close button positioning issue where X button overlaps drawer title
 *
 * Original: @praetorian-chariot/ui/Drawer
 * Migrated: 2025-10-20
 *
 * Changes from original:
 * - Fixed close button positioning from `-left-14` to `-left-20` to prevent overlap with drawer titles
 * - Added scrollResetKey prop for explicit scroll reset without remounting (2025-11-19)
 * - Maintained all other functionality and styling
 */

import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  variant?: 'parent' | 'child';
  escapeKey?: boolean;
  uuid?: string;
  scrollResetKey?: string; // When this changes, reset scroll to top
  initialWidth?: string; // Optional override for default width (e.g., '80%', '1200px')
}

export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  children,
  className,
  style,
  variant = 'parent',
  uuid: _uuid = '', // Kept for API compatibility, unused after jiggle removal
  escapeKey = false,
  scrollResetKey,
  initialWidth,
}) => {
  const isParent = variant === 'parent';
  const defaultWidth = isParent ? '60%' : 'calc(61% - 60px)';

  const [width, setWidth] = useState<string>(initialWidth ?? defaultWidth);
  const [isDragging, setIsDragging] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const zIndex = isParent ? 50 : 51;

  // Set the initial width of the drawer
  // Use setTimeout to defer setState, avoiding synchronous cascading renders
  useEffect(() => {
    if (open) {
      if (variant === 'child' || !width) {
        const timer = setTimeout(
          () => setWidth(initialWidth ?? defaultWidth),
          0
        );
        return () => clearTimeout(timer);
      }
    }
  }, [open, initialWidth, defaultWidth, variant]);

  // Handle dragging to resize the drawer
  const startResizing = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);

    const drawerElement = e.currentTarget.parentElement;
    if (!drawerElement) return;

    const currentWidth = drawerElement.getBoundingClientRect().width;
    const startX = e.pageX;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = startX - e.pageX;
      const newWidth = Math.max(
        300,
        Math.min(window.innerWidth * 0.8, currentWidth + deltaX)
      );
      setWidth(`${newWidth.toString()}px`);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const computedStyle = {
    ...style,
    width,
    zIndex,
  };

  // Reset scroll when scrollResetKey changes
  useEffect(() => {
    if (scrollResetKey !== undefined && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [scrollResetKey]);

  // Add escape key handling to close the drawer
  useEffect(() => {
    if (escapeKey) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && open) {
          onClose();
        }
      };
      window.addEventListener('keydown', handleEscape);
      return () => {
        window.removeEventListener('keydown', handleEscape);
      };
    }
  }, [open, onClose, escapeKey]);

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '20%' }}
      transition={{
        type: 'tween',
        duration: 0.2,
        ease: 'easeOut',
      }}
      className="pointer-events-none fixed inset-0"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div
          className={twMerge(
            'pointer-events-auto absolute inset-y-0 right-0',
            className,
            isDragging && 'select-none'
          )}
          style={computedStyle}
        >
          <div className="relative h-full">
            <div className="h-full">
              <div
                ref={scrollContainerRef}
                className="bg-chariot-background-primary border-chariot-stroke h-full overflow-x-hidden overflow-y-scroll border-l-2"
                data-testid="drawer-content"
              >
                <button
                  onClick={onClose}
                  className="border-chariot-stroke bg-chariot-background-primary hover:border-chariot-brand-primary absolute top-6 -left-[52px] flex size-10 items-center justify-center rounded-full border-2 transition-colors"
                  data-testid="drawer-close-button"
                >
                  <span className="sr-only">Close panel</span>
                  <X className="text-chariot-text-primary size-5" />
                </button>

                <div
                  onMouseDown={startResizing}
                  className="border-chariot-stroke bg-chariot-background-primary hover:border-chariot-brand-primary absolute top-1/2 -left-10 flex h-20 w-6 -translate-y-1/2 cursor-ew-resize items-center justify-center rounded-full border-2 transition-colors"
                  role="separator"
                  aria-label="Resize drawer"
                >
                  <div className="flex flex-row gap-1 p-2">
                    <div className="bg-chariot-text-primary h-6 w-0.5 rounded-full" />
                    <div className="bg-chariot-text-primary h-6 w-0.5 rounded-full" />
                  </div>
                </div>

                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

interface DrawersProps {
  children: React.ReactNode;
  disableBackdrop?: boolean;
}

export const Drawers: React.FC<DrawersProps> = ({
  children,
  disableBackdrop = false,
}) => {
  const childArray = React.Children.toArray(children);

  // Warn developers if attempting to open 3+ drawers
  if (childArray.length > 2) {
    console.warn(
      `[Drawers] Attempted to render ${childArray.length} drawers, but only 2 are supported. ` +
        `Oldest ${childArray.length - 2} drawer(s) will be hidden. ` +
        `Consider closing drawers before opening new ones.`
    );
  }

  // FIFO drawer limit: Only render last 2 drawers to prevent UI clutter
  // If 3+ drawers are queued, oldest drawers are dropped from rendering
  // To open a 3rd drawer, the application should close the oldest drawer first
  const lastTwoDrawers = childArray.slice(-2);
  const parentDrawer = lastTwoDrawers[0];
  const childDrawer = lastTwoDrawers[1];

  const hasParentAndChild =
    React.isValidElement<DrawerProps>(parentDrawer) &&
    React.isValidElement<DrawerProps>(childDrawer) &&
    parentDrawer.props.open &&
    childDrawer.props.open;

  return (
    <>
      {/* Parent Drawer */}
      <AnimatePresence>
        {React.isValidElement<DrawerProps>(parentDrawer) &&
          parentDrawer.props.open && (
            <Drawer
              {...parentDrawer.props}
              key="parent-drawer"
              variant="parent"
              escapeKey={!hasParentAndChild}
              style={{
                ...parentDrawer.props.style,
                width: hasParentAndChild ? 'calc(100% - 60px)' : '60%',
              }}
            />
          )}
      </AnimatePresence>

      {/* Backdrop */}
      <AnimatePresence>
        {hasParentAndChild && !disableBackdrop && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => {
              if (React.isValidElement<DrawerProps>(childDrawer)) {
                childDrawer.props.onClose();
              }
            }}
            className="bg-chariot-shade/50 pointer-events-auto fixed inset-0"
          />
        )}
      </AnimatePresence>

      {/* Child Drawer */}
      <AnimatePresence>
        {React.isValidElement<DrawerProps>(childDrawer) &&
          childDrawer.props.open && (
            <Drawer
              {...childDrawer.props}
              key={`child-drawer-${childDrawer.key ?? 'child'}`}
              variant="child"
              escapeKey={true}
              style={{
                width: 'calc(61% - 60px)',
                zIndex: 102,
              }}
            />
          )}
      </AnimatePresence>
    </>
  );
};
